import { getLocaleFromLang } from "helpers/language-helper";
import i18n, { t } from "i18next";
import { getSearchParams } from "search-params";
import { State, Time } from "store/store-types";
import { Contact } from "types/contact";
import { Customer } from "types/customer";

type ApiCustomer = Omit<Customer, "invoiceEmail"> & { email?: string };

export function mapCustomerForApi(customer: Customer): ApiCustomer {
    const { invoiceEmail, ...rest } = customer;
    return { ...rest, email: invoiceEmail };
}
import { PaymentMethod } from "types/payment";
import { ValidationResult } from "types/validation-result";
import { WaitListOffer, WaitListOfferStatus } from "types/wait-list-offer";
import { WebBooking } from "types/web-booking";

class disableConfirmationParameters {
    disableAllConfirmationMessages: boolean;
    disableEmailConfirmationMessage: boolean;
    disableSmsConfirmationMessage: boolean;
    constructor(values?: Partial<disableConfirmationParameters>) {
        this.disableAllConfirmationMessages = values?.disableAllConfirmationMessages ?? false;
        this.disableEmailConfirmationMessage = values?.disableEmailConfirmationMessage ?? false;
        this.disableSmsConfirmationMessage = values?.disableSmsConfirmationMessage ?? false;
    }
}

export class WaitListConfirmParameters extends disableConfirmationParameters {
    startTime: string;
    constructor(waitListOffers: WaitListOffer[], values?: Partial<disableConfirmationParameters>) {
        super(values);
        this.startTime =
            waitListOffers
                ?.find((offer) => offer.offerStatus === WaitListOfferStatus.Available)
                ?.startTime.format("YYYY-MM-DD[T]HH:mm:ss") ?? "";
    }
}

export class CreateReservationParameters {
    lang?: string;
    isPending?: boolean;
    isInquiry?: boolean;
    logMessage?: string;
    message?: string;
    timeSetDate?: string;
    isWaitList?: boolean;
    times?: { ruleId: number | null; start: string }[];
    guests?: number;
    guestsChildren?: number | null;
    turnstileToken?: string;
    /** Code from the confirmation sms, when the api asked for one. */
    twoFactorCode?: string;
    /** Asks for a new code instead of checking the one supplied. */
    resendTwoFactorCode?: boolean;
    contact?: Contact;
    customer?: ApiCustomer;
    wishToBeInvoiced?: boolean;
    invoiceMarking?: string;
    /** The invitation (VIP) link token the guest came with: lets them book ahead of the release schedule. */
    inviteToken?: string;

    constructor(state?: State, logMessage?: string, isInquiry?: boolean) {
        this.isPending = true;
        this.isInquiry = isInquiry;
        this.lang = getLocaleFromLang(i18n.language);
        /* Override msg or default message. */
        this.logMessage = logMessage ?? t("changeLogMessage.pendingLogMessage");
        this.message = state?.contact?.message ?? "";

        this.guests = state?.guests;
        this.guestsChildren = state?.guestsChildren ?? 0;
        this.isWaitList = state?.isWaitList;
        this.turnstileToken = state?.turnstileToken ?? undefined;
        this.twoFactorCode = state?.twoFactorCode ?? undefined;
        this.resendTwoFactorCode = state?.resendTwoFactorCode ?? undefined;
        this.contact = state?.contact;

        this.customer = state?.customer ? mapCustomerForApi(state.customer) : undefined;

        this.wishToBeInvoiced = state?.wishToBeInvoiced ?? false;
        this.invoiceMarking = state?.invoiceMarking ?? "";

        /* TimeSetDate = the selected booking date */
        this.timeSetDate = state?.date?.format("YYYY-MM-DD[T]HH:mm:ss") ?? "";
        this.inviteToken = getSearchParams().invite || undefined;
        this.times =
            state?.times?.map((time: Time) => {
                return {
                    ruleId: time.ruleId,
                    start: time.start.format("YYYY-MM-DD[T]HH:mm:ss"),
                };
            }) ?? [];
    }
}

export type removeTime = {
    sectionId: number;
    startTime: IMoment;
};
export class UpdateReservationParameters extends CreateReservationParameters {
    bookingChargeId: string;
    removeTimes: { sectionId: number; startTime: string }[];
    replaceTimes: boolean = true;
    contact?: Contact;
    constructor(
        state: State,
        timesToUpdate: Time[],
        removeTimes?: removeTime[],
        message?: string,
        isPending?: boolean,
        isInquiry?: boolean
    ) {
        super(state);
        this.message = message ?? "";
        this.bookingChargeId = "";
        this.logMessage = t("changeLogMessage.editedLogMessage");
        this.times =
            timesToUpdate.map((time: Time) => {
                return {
                    ruleId: time.ruleId,
                    start: time.start.format("YYYY-MM-DD[T]HH:mm:ss"),
                };
            }) ?? [];
        this.removeTimes =
            removeTimes?.map((time) => {
                return {
                    sectionId: time.sectionId,
                    startTime: time.startTime.format("YYYY-MM-DD[T]HH:mm:ss"), // Throw away timezone
                };
            }) ?? [];
        this.contact = state.contact;
        this.isPending = isPending;
        this.isInquiry = isInquiry;
    }
}

export class PaymentTerminalParameters {
    terminalReturnUrl: string;
    payMethod: PaymentMethod;
    contact: Contact | null;
    customer: Customer | null;
    message: string;
    // z: string;
    // invoiceMarking: "",

    constructor(returnUrl: string, state?: State, webBooking?: WebBooking) {
        this.terminalReturnUrl = returnUrl ?? "";
        this.payMethod = state?.payment?.payMethod ?? PaymentMethod.CreditCard;
        this.contact = state?.contact ?? webBooking?.contact ?? null;
        this.customer = state?.customer ?? webBooking?.customer ?? null;
        this.message = state?.contact?.message ?? "";
    }
}

export interface BackFromPaymentTerminalParameters {
    bookingChargeId: string;
    startTime: string | null;
}
export class BackFromTerminalResponse {
    result: WebBooking | null;
    validationResult: ValidationResult;

    constructor(values: BackFromTerminalResponse) {
        this.result = values.result ?? null;
        this.validationResult = values.validationResult
            ? new ValidationResult(values.validationResult)
            : new ValidationResult();
    }
}
